import type { Locale } from 'date-fns';
import {
  addMinutes,
  addHours,
  addDays,
  addSeconds,
  formatDistanceStrict,
  format,
  isValid,
  parse,
} from 'date-fns';
import { isBrowser, warning } from '@adeira/js';
import dateFnsLanguageMap from '@kiwicom/account-web/content/dateFnsLanguageMap';

import { SECONDS_IN_DAY, SECONDS_IN_HOUR, SECONDS_IN_MINUTE } from './consts';

type Args = {
  readonly durationInSeconds: number;
  readonly locale?: string;
  readonly isShorten?: boolean;
  readonly displayZeroValues?: {
    days: boolean;
    hours: boolean;
    minutes: boolean;
    seconds: boolean;
  };
};

const shortenTimeDuration = (timeDuration: string): string => {
  const isWordRegex = /\D+/;
  const leadingSpaceAndCharRegex = /(?:\s\D)/g;
  return timeDuration
    .split(' ')
    .reduce((acc, item) => {
      if (isWordRegex.test(item)) {
        return [...acc, item[0]];
      }

      return [...acc, item];
    }, [])
    .join(' ')
    .replace(leadingSpaceAndCharRegex, (x) => {
      return x.trim();
    });
};

export const getDatefnsLocale = (locale: string | null | undefined): Locale => {
  if (isBrowser() === true) {
    return window.dateLocale;
  }

  const dateFnsLocalString = dateFnsLanguageMap[locale ?? 'en'];
  // Preventing it from entering client bundle
  // eslint-disable-next-line no-eval
  return eval(`require('date-fns/locale/${dateFnsLocalString}')`);
};

export const getHumanizedTimeDuration = ({
  durationInSeconds,
  locale,
  isShorten = false,
  displayZeroValues = {
    days: false,
    hours: false,
    minutes: false,
    seconds: false,
  },
}: Args): string => {
  if (durationInSeconds === 0 || isNaN(durationInSeconds)) {
    return '0';
  }
  const datefnsLocale = getDatefnsLocale(locale);
  const now = new Date();
  const days = Math.floor(durationInSeconds / SECONDS_IN_DAY);
  const hours = Math.floor((durationInSeconds % SECONDS_IN_DAY) / SECONDS_IN_HOUR);
  const minutes = Math.floor((durationInSeconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE);
  const seconds = durationInSeconds % SECONDS_IN_MINUTE;

  let future = addDays(now, days);

  const localizedDays = formatDistanceStrict(now, future, {
    locale: datefnsLocale,
    unit: 'day',
  });

  future = addHours(now, hours);
  const localizedHours = formatDistanceStrict(now, future, {
    locale: datefnsLocale,
    unit: 'hour',
  });
  future = addMinutes(now, minutes);
  const localizedMinutes = formatDistanceStrict(now, future, {
    locale: datefnsLocale,
    unit: 'minute',
  });
  future = addSeconds(now, seconds);
  const localizedSeconds = formatDistanceStrict(now, future, {
    locale: datefnsLocale,
    unit: 'second',
  });

  let result = '';
  if (isShorten) {
    // shorten each segment separately - it prevents concatenating segments without numbers
    // eg. cz lang: '1h 1m 1s' gets shortened to '1h 1ms' but should be '1h 1m s'
    result += `${shortenTimeDuration(displayZeroValues.days || days > 0 ? localizedDays : '')}`;
    result += ` ${shortenTimeDuration(displayZeroValues.hours || hours > 0 ? localizedHours : '')}`;
    result += ` ${shortenTimeDuration(
      displayZeroValues.minutes || minutes > 0 ? localizedMinutes : '',
    )}`;
    result += ` ${shortenTimeDuration(
      displayZeroValues.seconds || seconds > 0 ? localizedSeconds : '',
    )}`;
  } else {
    result += `${displayZeroValues.days || days > 0 ? localizedDays : ''}`;
    result += ` ${displayZeroValues.hours || hours > 0 ? localizedHours : ''}`;
    result += ` ${displayZeroValues.minutes || minutes > 0 ? localizedMinutes : ''}`;
    result += ` ${displayZeroValues.seconds || seconds > 0 ? localizedSeconds : ''}`;
  }
  const formattedResult = result.replace(/\s\s+/g, ' ').trim();

  return formattedResult;
};

export const padDate = (date: string | null | undefined): string | null => {
  // Performing the padding on empty string returns "00"
  // new Date("00") is valid in chrome and node, but not in Firefox 🤷‍♂️
  if (date == null || date === '') {
    return null;
  }

  return date
    .split('-')
    .map((dateField, index) => {
      // We need to pad also year as it is causing problems
      // when we render different UI while user types it
      // more here: https://skypicker.slack.com/archives/C02E8AFT2F3/p1646065283344299
      if (index === 0) {
        return dateField.padStart(4, '0');
      }
      return dateField.padStart(2, '0');
    }) // new Date('2020-2-10') does not work in safari. This should safeguard us from these errors
    .join('-');
};

export const formatDate = (
  date: string | null | undefined,
  formatString: string,
  locale: string,
  shouldCountTimezoneOffset?: boolean | undefined,
): string => {
  const datefnsLocale = getDatefnsLocale(locale);
  const paddedDate = padDate(date);
  const errorValue = '';

  if (paddedDate !== null && !isValid(new Date(paddedDate))) {
    warning(true, `Date formatter got passed invalid date`);
    return errorValue;
  }
  if (shouldCountTimezoneOffset && paddedDate !== null) {
    const date = new Date(paddedDate);
    // Add timezone offset to make sure dates like dob, expiration dates don't get changes in different timezones
    // valueOf returns milliseconds, getTimezoneOffset returns minutes, 1min = 60k ms
    return format(new Date(date.valueOf() + date.getTimezoneOffset() * 60000), formatString, {
      locale: datefnsLocale,
    });
  }

  if (paddedDate !== null) {
    return format(new Date(paddedDate), formatString, {
      locale: datefnsLocale,
    });
  }

  warning(true, `Date formatter got passed invalid date`);
  return errorValue;
};
export const isValidDate = (str?: string): boolean => {
  try {
    // validating by isValid(new Date(str)) is not possible, accepts LOLs like "--20" or "2010-02-31"
    return isValid(parse(str ?? '', 'yyyy-M-dd', new Date()));
  } catch (error) {
    return false;
  }
};
export const getFormattedDateRange = ({
  dateFrom,
  dateTo,
  locale,
}: {
  dateFrom: string;
  dateTo?: string | null | undefined;
  locale: string;
}): string => {
  if (dateFrom === dateTo) {
    return `${formatDate(dateFrom, 'eee d/MM', locale)}`;
  }
  if (dateTo != null) {
    return `${formatDate(dateFrom, 'eee d/MM', locale)} - ${formatDate(
      dateTo,
      'eee d/MM',
      locale,
    )}`;
  }
  return formatDate(dateFrom, 'eee d/MM', locale);
};

export const dateTimeToDate = (
  dateTimeString: string | null | undefined,
): string | null | undefined => {
  if (dateTimeString == null) {
    return null;
  }

  // This function gets the DateTime string from graphql, and transforms it to the Date type that graphql returns
  // 2020-11-28T15:06:03.081561+00:00 -> 2020-11-28
  // We have some fields that are DateTime when we just need Date. This complicates things because of the nature of javascript dates
  const match = /(?<date>\d{4}-\d{2}-\d{2})/.exec(dateTimeString);
  const dateString = match?.groups?.date;
  return dateString;
};
